<template>
  <div>
    <div ref="errorsRef">
      <BErrors :errors="errors" class="mb-4" />
    </div>

    <form @submit.prevent="submitNew">
      <BCard
        :title="title"
        :subtitle="subtitle"
        class="mb-6"
      >
        <div class="sm:grid grid-cols-2 gap-3">
          <UFormGroup
            v-for="field in primaryFields"
            :key="field.field"
            :label="!field.custom ? $t('general.'+field.field) : field.label"
            :required="field.required"
            :class="fieldColumns(field)"
            class="mb-4"
            :error="isEmptyRequiredField(field)"
            v-slot="{ error }"
          >
            <ElementsAddress
              v-if="field.field === 'address'"
              v-model="newCustomerData.fields"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              :required-fields="addressRequiredFields"
              flat
            />

            <ElementsTitle
              v-else-if="field.field === 'title'"
              v-model="newCustomerData.fields[field.field]"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              flat
            />

            <FieldsDate
              v-else-if="field.field === 'dateOfBirth'"
              v-model="newCustomerData.fields[field.field]"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              flat
            />
            
            <component
              :is="field.component"
              v-else-if="field.custom"
              v-model="newCustomerData.customFields[field.customField!.id]"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              :error="error"
              flat
            />
            <component
              :is="field.component"
              v-else
              v-model="newCustomerData.fields[field.field]"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              :error="error"
              flat
            />

            <p v-if="props.fromBooking && !isAppLayout && field.field === 'email'" class="text-xs text-secondary/70 cursor-pointer mt-1">
              <a @click="emit('newSearch')">{{ $t('components.customerSelector.return') }}</a>
            </p>
          </UFormGroup>
        </div>

        <UButton v-if="!showMoreFields" type="button" variant="soft" class="my-3" @click="showMoreFieldsClicked">
          {{ $t('general.more') }}
        </UButton>

        <div v-if="showMoreFields" class="sm:grid grid-cols-2 gap-3 mt-6">
          <UFormGroup
            v-for="field in secondaryFields"
            :key="field.field"
            :label="!field.custom ? $t('general.'+field.field) : field.label"
            :required="field.required"
            :class="fieldColumns(field)"
          >
            <ElementsAddress
              v-if="field.field === 'address'"
              v-model="newCustomerData.fields"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              :required-fields="addressRequiredFields"
              flat
            />

            <ElementsTitle
              v-else-if="field.field === 'title'"
              v-model="newCustomerData.fields[field.field]"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              flat
            />

            <FieldsDate
              v-else-if="field.field === 'dateOfBirth'"
              v-model="newCustomerData.fields[field.field]"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              flat
            />

            <component
              :is="field.component"
              v-else-if="field.custom"
              v-model="newCustomerData.customFields[field.customField!.id]"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              flat
            />
            
            <component
              :is="field.component"
              v-else
              v-model="newCustomerData.fields[field.field]"
              v-bind="field.componentProps"
              :disabled="field.disabled"
              flat
            />

            <p v-if="field.help" class="text-xs text-secondary/70 cursor-pointer mt-1" v-html="field.help" />
          </UFormGroup>
        </div>
      </BCard>

      <!-- Privacy consent -->
      <BCard v-if="response.consentRequired" :title="response.consentTitle || 'Privacy consent'" :subtitle="$t('general.fieldIsRequired',{field: response.consentTitle})" class="mb-6">
        <p v-if="response.consentContent" class="mb-4 text-sm text-secondary" v-html="formatHtml(response.consentContent)" />

        <BCheckbox v-model="newCustomerData.privacyConsentAccepted" :label="response.consentAcceptLabel || 'Accept privacy consent'" />
      </BCard>

      <!-- Marketing consent -->
      <BCard v-if="response.marketingConsentRequired" :title="response.marketingConsentTitle || 'Marketing consent'" class="mb-6">
        <p v-if="response.marketingConsentContent" class="mb-4 text-sm text-secondary" v-html="formatHtml(response.marketingConsentContent)" />

        <BCheckbox v-model="newCustomerData.marketingConsentAccepted" :label="response.marketingConsentAcceptLabel || 'Accept marketing consent'" />
      </BCard>

      <UButton type="submit" variant="soft" block :loading="loading">
        {{ $t('general.create') }} »
      </UButton>
    </form>
  </div>
</template>

<script setup lang="ts">
import Utils from '@/utils'
import type { ApiResponse } from '@/api/controller';
import type { NewCustomerData, Field, CombinedField } from '@@/types/api';

const { $api, $store, $i18n } = useNuxtApp()

const emit = defineEmits(['success', 'newSearch'])

interface Props {
  title?: string
  subtitle?: string
  response: ApiResponse<any>
  create: boolean
  newCustomerDataProp?: NewCustomerData
  fromBooking?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  create: false,
  newCustomerDataProp: () => ({
    fields: {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      dateOfBirth: null,
    },
    customFields: [],
    privacyConsentAccepted: false,
    marketingConsentAccepted: false
  }),
  fromBooking: false
})

const errors = ref<Array<string>>([])
const errorsRef = ref<HTMLElement | null>(null)
const newCustomerData = ref(props.newCustomerDataProp)
const showMoreFields = ref<boolean>(false)
const loading = ref<boolean>(false)

const isEmptyRequiredField = (field: CombinedField) => {
  if (errors.value.length > 0 && field.required) {
    if (field.custom) {
      return !newCustomerData.value.customFields[field.customField!.id]
    } else if (field.field === 'address') {
      return !newCustomerData.value.fields
    } else {
      return !newCustomerData.value.fields[field.field]
    }
  }
}

const isAddressFieldEnabled = computed(() => {
  return props.response.fields?.some((field: any) => {
    return field.field === 'address' && field.show
  })
})

const addressRequiredFields = computed(() => {
  const subFields = ['address2', 'suburb', 'city', 'region', 'postcode', 'country']
  const required: Record<string, boolean> = {}
  if (props.response?.fields) {
    for (const field of props.response.fields) {
      if (subFields.includes(field.field)) {
        required[field.field] = field.required
      }
    }
  }
  return required
})

const primaryFields = computed<CombinedField[]>(() => {
  if (props.response && props.response.fields) {
    return props.response.fields.filter((field: any) => {
      // If address field is enabled, we should hide other address fields
      if (isAddressFieldEnabled.value) {
        if (['address2', 'suburb', 'city', 'region', 'postcode', 'country'].includes(field.field)) {
          return false
        }
      }

      return field.show && field.primary
    })
  }
  return []
})

const secondaryFields = computed<CombinedField[]>(() => {
  if (props.response && props.response.fields) {
    return props.response.fields.filter((field: any) => {

      // If address field is enabled, we should hide other address fields
      if (isAddressFieldEnabled.value) {
        if (['address2', 'suburb', 'city', 'region', 'postcode', 'country'].includes(field.field)) {
          return false
        }
      }

      return field.show && !field.primary
    })
  }
  return []
})

const isAppLayout = computed(() => {
  return $store.getters.layout === 'app'
})

const formatHtml = (text: string) => {
  return Utils.text.formatHtml(text)
}

const fieldColumns = (field: any) => {
  if (field.type === 'textarea' || field.field === 'address') {
    return 'col-span-2'
  } else {
    return ''
  }
}

const showMoreFieldsClicked = () => {
  showMoreFields.value = true
}

const submitNew = async (e: Event) => {
  loading.value = true
  errors.value = []

  if (newCustomerData.value.fields?.email) {
    const sanitizedEmail = Utils.validation.sanitizeEmail(newCustomerData.value.fields.email)
    newCustomerData.value.fields.email = sanitizedEmail

    if (sanitizedEmail && !Utils.validation.email(sanitizedEmail)) {
      errors.value.push($i18n.t('pages.customer.login.index.validateEmail'))
    }
  }

  if (props.response.fields) {
    for (let i = 0; i < props.response.fields.length; i++) {
      const field: Field = props.response.fields[i]

      if (field.custom) {
        if (
          field.required &&
          field.customField &&
          !newCustomerData.value.customFields[field.customField.id]
        ) {
          errors.value.push(
            $i18n.t('general.fieldIsRequired', { field: field.label })
          )
        }
      } else if (
        field.required &&
        !newCustomerData.value.fields[field.field]
      ) {
        const key = $i18n.t('general.' + field.field)
        errors.value.push($i18n.t('general.fieldIsRequired', { field: key }))
      }
    }
  }

  if (
    props.response.consentRequired &&
    !newCustomerData.value.privacyConsentAccepted
  ) {
    errors.value.push(
      $i18n.t('general.fieldIsRequired', {
        field: props.response.consentTitle
      })
    )
  }

  if (errors.value.length > 0) {
    if (errorsRef.value) {
      errorsRef.value.scrollIntoView({ behavior: 'smooth' })
    }
    loading.value = false
    return false
  }

  try {
    let res

    if (props.create) {
      res = await $api.booking.newCustomer(
        newCustomerData.value.fields,
        newCustomerData.value.customFields,
        newCustomerData.value.privacyConsentAccepted,
        newCustomerData.value.marketingConsentAccepted
      )
    } else {
      res = await $api.booking.validateCustomer(
        newCustomerData.value.fields,
        newCustomerData.value.customFields,
        newCustomerData.value.privacyConsentAccepted,
        newCustomerData.value.marketingConsentAccepted
      )
    }

    if (res) {
      if (res.success && res.customer) {
        const customer = res.customer
        customer.fields = newCustomerData.value.fields
        customer.customFields = newCustomerData.value.customFields
        customer.privacyConsentAccepted = newCustomerData.value.privacyConsentAccepted
        customer.marketingConsentAccepted = newCustomerData.value.marketingConsentAccepted
  
        // this.selectCustomer(customer)
        emit('success', customer)
      } else {
        for (let i = 0; i < res.errors.length; i++) {
          errors.value.push(res.errors[i])
        }
      }
    }

    loading.value = false
  } catch (error) {
    if (errorsRef.value) {
      errorsRef.value.scrollIntoView({ behavior: 'smooth' })
    }
    loading.value = false
    // console.log(error)
    errors.value = [$i18n.t('general.errorRequest')]
  }
}
</script>
